import React, { useState } from 'react';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import {
  exportQuestions,
  generateQuestions,
  getJob,
} from '../../../../utils/api_service/job_api';
import { fetchSkills } from '../../../../utils/redux_store/features/skill_reducer';
import { fetchSubSkills } from '../../../../utils/redux_store/features/subskill_reducer';
import { store } from '../../../../utils/redux_store/store';
import {
  QuestionGeneratorJob,
  Questions,
} from '../../../../utils/types/content_manager_types';

type QuestionListProps = {
  job: QuestionGeneratorJob;
  questionsLoading: boolean;
  setJob: (newJobData: QuestionGeneratorJob) => void;
  errorJob: boolean;
};

export const QuestionsList = ({
  job,
  questionsLoading,
  setJob,
  errorJob,
}: QuestionListProps) => {
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [generateMoreLoading, setgenerateMoreLoading] =
    useState<boolean>(false);
  const [oldQuestionsCount, setOldQuestionsCount] = useState<number>(
    job.questions?.length || 0,
  );
  const [errorGenerateJob, setErrorGenerateJob] = useState<boolean>(false);
  const [questionImportList, setQuestionImportList] = useState<Array<string>>(
    [],
  );
  const allSelected = job.questions
    ?.filter((question) => question.exists === 'False')
    .every((question) => questionImportList.includes(question.questionId));

  const handleQuestionClick = (id: string) => {
    if (questionImportList.includes(id)) {
      setQuestionImportList(questionImportList.filter((item) => item !== id));
    } else {
      setQuestionImportList([...questionImportList, id]);
    }
  };

  const handleSelectAllClick = () => {
    if (allSelected) {
      setQuestionImportList([]);
    } else {
      const allQuestionIds =
        job.questions
          ?.filter((question) => question.exists === 'False')
          .map((question) => question.questionId) || [];
      setQuestionImportList(allQuestionIds);
    }
  };

  const pollJob = (id: string) => {
    getJob(id)
      .then((response) => {
        setJob(response);
        if (response.status && response.status.includes('Error')) {
          setErrorGenerateJob(true);
        } else {
          if (response.status !== 'Generation Completed...') {
            setTimeout(() => {
              pollJob(id);
            }, 5000);
          } else {
            setgenerateMoreLoading(false);
          }
        }
      })
      .catch(() => {
        toast.error('Error. Please try again', { position: 'bottom-center' });
        setErrorGenerateJob(true);
      });
  };

  const generateMore = () => {
    if (job.questions) {
      setOldQuestionsCount(job.questions?.length);
    }
    setgenerateMoreLoading(true);
    generateQuestions(job.id).then((response) => {
      if (response.ok) {
        pollJob(job.id);
      }
    });
  };

  const exportToQuestionBank = () => {
    setExportLoading(true);
    try {
      exportQuestions(job.id, questionImportList)
        .then((response) => {
          setJob(response);
          setQuestionImportList([]);
          store.dispatch(fetchSkills());
          store.dispatch(fetchSubSkills());
        })
        .catch(() => {
          toast.error('Error importing questions', {
            position: 'bottom-center',
          });
        });
    } catch (error) {
      toast.error('Error importing questions', { position: 'bottom-center' });
    }
    setExportLoading(false);
  };

  const SkeletonLoader = () => (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <p
            className={`text-sm font-extrabold ${
              errorJob ? 'text-red-500' : 'text-emerald-700'
            }`}
        >
          { generateMoreLoading && job.status === 'Generation Completed...'
            ? 'Generating More Questions...'
            : job.status }
        </p>
      </div>
      <div className='animate-pulse'>
        { [...new Array(5)].map((_, index) => (
          <div
              key={index}
              className='border-4 mb-5 border-gray-300 p-4 rounded-md'
          >
            <div className='h-8 p-4 bg-gray-300 rounded mb-2'></div>
            <div className='h-8 p-4 bg-gray-300 rounded w-3/4'></div>
          </div>
        )) }
      </div>
    </div>
  );

  return (
    <div className='rounded-lg border border-slate-200 bg-white p-3 shadow-sm'>
      { questionsLoading ? (
        <SkeletonLoader />
      ) : job.questions && job.questions.length > 0 ? (
        <>
          <div className='mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <h3 className='text-sm font-extrabold text-slate-900'>
                Generated Questions
              </h3>
              <p className='text-xs text-slate-500'>
                Select new questions to import into the question bank.
              </p>
            </div>
            <Button
                onClick={() => handleSelectAllClick()}
                variant='secondary'
                label={allSelected ? 'Unselect All' : 'Select All'}
            />
          </div>
          <div className='space-y-2'>
            { job.questions.map((question: Questions) => {
              const isSelected = questionImportList.includes(
                question.questionId,
              );
              const newQuestion =
                parseInt(question.questionId) > oldQuestionsCount;
              const selectable = question.exists === 'False';

              return (
                <div
                    className={`rounded-lg border p-3 transition ${
                      isSelected
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    } ${selectable ? 'cursor-pointer' : ''}`}
                    key={question.questionId}
                    onClick={() => {
                      if (selectable) {
                        handleQuestionClick(question.questionId);
                      }
                    }}
                >
                  <div className='flex flex-col gap-3 lg:flex-row lg:items-start'>
                    <div className='flex-1 space-y-2'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600'>
                          #{ question.questionId }
                        </span>
                        { newQuestion && (
                          <span className='rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700'>
                            New
                          </span>
                        ) }
                        { question.exists === 'Exists' && (
                          <span className='rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700'>
                            Already exists
                          </span>
                        ) }
                        { question.exists === 'Added' && (
                          <span className='rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700'>
                            Added
                          </span>
                        ) }
                      </div>
                      <div>
                        <span className='text-[11px] font-extrabold uppercase tracking-wide text-slate-500'>
                          Question
                        </span>
                        <p className='mt-1 text-sm font-semibold text-slate-900'>
                          { question.question }
                        </p>
                      </div>
                      <div>
                        <span className='text-[11px] font-extrabold uppercase tracking-wide text-slate-500'>
                          Answer
                        </span>
                        <p className='mt-1 text-xs leading-5 text-slate-600'>
                          { question.answer }
                        </p>
                      </div>
                    </div>
                    <div className='flex min-w-0 flex-wrap gap-2 lg:w-72'>
                      { [question.skill, question.subSkill1, question.subSkill2, question.subSkill3]
                        .filter(Boolean)
                        .map((skill) => (
                          <span
                              key={skill}
                              className='max-w-full truncate rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700'
                          >
                            { skill }
                          </span>
                        )) }
                    </div>
                  </div>
                </div>
              );
            }) }
          </div>
          <div className='mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between'>
            <div className='text-sm font-semibold text-slate-600'>
              { questionImportList.length } questions selected
            </div>
            <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
              <Button
                  onClick={() => generateMore()}
                  variant='primary'
                  label={
                    generateMoreLoading ? 'Generating...' : 'Generate More'
                  }
                  disabled={job.questions.length > 29 || generateMoreLoading}
              />
              <Button
                  onClick={() => exportToQuestionBank()}
                  variant='primary'
                  label={
                    exportLoading ? 'Exporting...' : 'Add to Question Bank'
                  }
                  disabled={questionImportList.length === 0 || exportLoading}
              />
            </div>
          </div>
          { generateMoreLoading && !errorGenerateJob && <SkeletonLoader /> }
        </>
      ) : (
        <div className='flex min-h-[120px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center'>
          <p className='text-sm font-bold text-slate-700'>
            No generated questions yet
          </p>
          <p className='mt-1 max-w-md text-xs text-slate-500'>
            Questions will appear here when this content generation job completes.
          </p>
        </div>
      ) }
    </div>
  );
};
